import type { Tax } from '@monorepo/shared/schemas/taxes'
import { Building2, DollarSign, Percent, Trash2, User } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/shared/components/ui/alert-dialog'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'

export interface AssignedTax extends Tax {
  priority: number
  is_active: boolean
  assigned_at: string
  source?: 'customer' | 'organization'
}

export interface AssignedTaxesListProps {
  taxes: AssignedTax[]
  onRemove?: (taxId: string) => void
  readonly?: boolean
  showSource?: boolean
  emptyMessage?: string
}

export function AssignedTaxesList({
  taxes,
  onRemove,
  readonly = false,
  showSource = false,
  emptyMessage = 'No taxes assigned',
}: AssignedTaxesListProps) {
  const formatTaxRate = (tax: Tax) => {
    if (tax.tax_type === 'percentage' && tax.percentage_value) {
      return `${tax.percentage_value}%`
    }
    if (tax.tax_type === 'fixed' && tax.fixed_amount_cents) {
      const amount = (tax.fixed_amount_cents / 100).toFixed(2)
      return `${tax.currency || 'USD'} ${amount}`
    }
    if (tax.tax_type === 'compound') {
      return 'Compound'
    }
    return '-'
  }

  const getTaxIcon = (tax: Tax) => {
    return tax.tax_type === 'percentage' ? Percent : DollarSign
  }

  const getSourceInfo = (tax: AssignedTax) => {
    if (!tax.source) return null

    if (tax.source === 'customer') {
      return {
        icon: User,
        label: 'Customer-specific',
        variant: 'default' as const,
      }
    }

    return {
      icon: Building2,
      label: 'From Organization',
      variant: 'secondary' as const,
    }
  }

  if (taxes.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center rounded-lg border border-dashed p-12 text-center'>
        <div className='flex h-12 w-12 items-center justify-center rounded-full bg-muted'>
          <Percent className='h-6 w-6 text-muted-foreground' />
        </div>
        <h3 className='mt-4 text-sm font-medium'>No taxes assigned</h3>
        <p className='mt-2 text-sm text-muted-foreground'>{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className='rounded-lg border'>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className='w-[80px]'>Priority</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Code</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Rate/Amount</TableHead>
            {showSource && <TableHead>Source</TableHead>}
            <TableHead>Jurisdiction</TableHead>
            <TableHead>Status</TableHead>
            {!readonly && <TableHead className='w-[100px]'>Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {taxes.map(tax => {
            const Icon = getTaxIcon(tax)
            const sourceInfo = showSource ? getSourceInfo(tax) : null

            return (
              <TableRow key={tax.id}>
                <TableCell>
                  <Badge variant='outline'>#{tax.priority}</Badge>
                </TableCell>
                <TableCell className='font-medium'>{tax.name}</TableCell>
                <TableCell>
                  <code className='rounded bg-muted px-2 py-1 text-xs'>
                    {tax.code}
                  </code>
                </TableCell>
                <TableCell>
                  <div className='flex items-center gap-2'>
                    <Icon className='h-4 w-4 text-muted-foreground' />
                    <span className='capitalize text-sm'>{tax.tax_type}</span>
                  </div>
                </TableCell>
                <TableCell>{formatTaxRate(tax)}</TableCell>
                {showSource && sourceInfo && (
                  <TableCell>
                    <Badge variant={sourceInfo.variant} className='gap-1'>
                      <sourceInfo.icon className='h-3 w-3' />
                      {sourceInfo.label}
                    </Badge>
                  </TableCell>
                )}
                {showSource && !sourceInfo && <TableCell>-</TableCell>}
                <TableCell>
                  {tax.jurisdiction ? (
                    <Badge variant='outline' className='text-xs'>
                      {tax.jurisdiction}
                    </Badge>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  {tax.is_active ? (
                    <Badge variant='default' className='bg-green-500'>
                      Active
                    </Badge>
                  ) : (
                    <Badge variant='secondary'>Inactive</Badge>
                  )}
                </TableCell>
                {!readonly && (
                  <TableCell>
                    {onRemove && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant='ghost'
                            size='sm'
                            className='h-8 w-8 p-0 text-destructive hover:text-destructive'
                          >
                            <Trash2 className='h-4 w-4' />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              Remove tax assignment?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to remove "{tax.name}" from
                              this{' '}
                              {showSource && tax.source === 'customer'
                                ? 'customer'
                                : 'entity'}
                              ? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => onRemove(tax.id)}
                              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
                            >
                              Remove
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </TableCell>
                )}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
